import React from 'react';
import Link from 'next/link';
import { useForm, useWatch } from 'react-hook-form';
import {
	Alert,
	Separator,
	Spinner,
	Typography,
	Button,
} from 'components/common';
import { InputCheck, InputEmail } from 'components/form';
import InputPassword from 'components/form/input-password/input-password';
import NextAuthProvidersEnum from 'const/auth';
import AppRoutes from 'const/routes';
import { useSignIn } from '@/hooks';
import { getLocalStorage, setLocalStorage } from '@/utils/localStorage';
import { decrypt } from '@/lib/crypto';
import clsx from 'clsx';
import { AuthLayout } from '@/components/layout/layout/AuthLayout';
import { AuthCard } from '@/components/auth/AuthCard';
import { ResetCard } from '@/components/auth/ResetCard';
import Icons from '@/const/icons';
interface SignInProps {
	providers: NextAuthProvidersEnum[];
}

export const SignInScreen: React.FC<SignInProps> = () => {
	// Utils
	const isStaging = process.env.NEXT_PUBLIC_SERVER === 'staging';
	const isDev = process.env.NEXT_PUBLIC_SERVER === 'develop';

	const [isRememberMe, setIsRememberMe] = React.useState<boolean>(false);
	const [recaptchaValid, setRecaptchaValid] = React.useState<boolean | null>(
		null
	);

	const {
		errorMessage,
		isLoadingScreen,
		isSocialReactivated,
		isSocialRecover,
		isVerified,
		showDeactivatedMessage,
		showErrorsRender,
		showReactivateAccountLink,
		handleSubmitData,
		isLoading,
		handleBackSocialReactivated,
	} = useSignIn();

	const {
		register,
		handleSubmit,
		formState: { errors },
		setValue,
		control,
		clearErrors,
	} = useForm({ mode: 'onSubmit' });

	const formValues = useWatch({
		control,
	});

	// Inputs rules
	const rules = {
		email: {
			required: { value: true, message: 'This is required.' },
		},
		password: {
			required: { value: true, message: 'This is required.' },
		},
	};

	React.useEffect(() => {
		if (isStaging || isDev) {
			setRecaptchaValid(true);
		}
	}, [isStaging, isDev]);

	React.useEffect(() => {
		if (formValues.email || formValues.password) clearErrors(); // remove the errors when user types
	}, [formValues]);

	React.useEffect(() => {
		if (isSocialRecover) {
			setLocalStorage('social-recover', true);
		}
	}, [isSocialRecover]);

	React.useEffect(() => {
		showErrorsRender();
	}, [showErrorsRender]);

	React.useEffect(() => {
		if (typeof window !== 'undefined') {
			// Remember me functionality
			const rememberMe = getLocalStorage('client_rememberme');
			const password = getLocalStorage('client_password');
			const email = getLocalStorage('client_email');
			setIsRememberMe(rememberMe);
			if (password && email && rememberMe) {
				setValue('rememberme', rememberMe, {
					shouldDirty: true,
					shouldValidate: true,
				});
				setValue('email', email, {
					shouldDirty: true,
					shouldValidate: true,
				});
				const passwordDescrypted = decrypt(password);
				setValue('password', passwordDescrypted, {
					shouldDirty: true,
					shouldValidate: true,
				});
			}
		}
	}, []);

	const onChangeRecaptcha = (): void => {
		setRecaptchaValid(true);
	};

	const getTitle = (): string => {
		if (isSocialRecover) return 'Reactivate your Account';
		if (isSocialReactivated)
			return 'Your Account Has Been Successfully Activated';
		return 'Welcome Back';
	};

	return (
		<AuthLayout withCarrousel={!isSocialRecover && !isSocialReactivated}>
			{isSocialRecover || isSocialReactivated ? (
				<ResetCard
					title={getTitle()}
					srcImage={
						isSocialReactivated
							? '/img/landing/svg/black-magic-woman-alt.svg'
							: undefined
					}
					handleBack={handleBackSocialReactivated}
					classNameTitle="!text-4xl lg:px-6"
				>
					{isLoadingScreen ? (
						<Spinner type="loadingPage" className="m-auto mt-10" />
					) : (
						<>
							{!isSocialReactivated && (
								<>
									<Typography type="regular-16" className="text-center">
										It looks like you previously deleted your account. <br />
										To reactivate it, please select the account you originally{' '}
										<br />
										signed-up with below.
									</Typography>
									<Separator text="Select your account" className="my-8" />
								</>
							)}

							{isSocialReactivated && (
								<div>
									<Spinner type="loadingPage" className="m-auto mt-10" />
									<Typography
										type="regular-16"
										className="text-center opacity-60"
									>
										Redirecting you to the Claim Magic application in a few
										seconds
									</Typography>
								</div>
							)}

							{!isSocialReactivated && (
								<div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
									<Button
										size="full"
										onClick={() =>
											handleSubmitData(NextAuthProvidersEnum.GOOGLE)
										}
										disabled={isLoading}
										icon={Icons.google}
										iconLeft
										label="Sign in with Google"
										className="!py-4"
									/>
									<Button
										size="full"
										onClick={() =>
											handleSubmitData(NextAuthProvidersEnum.MICROSOFT)
										}
										disabled={isLoading}
										icon={Icons.microsoft}
										iconLeft
										label="Sign in with Microsoft"
										className="!py-4"
									/>
								</div>
							)}
						</>
					)}
				</ResetCard>
			) : (
				<AuthCard
					title={getTitle()}
					disabled={isLoading || !recaptchaValid}
					buttonLabel="Sign In"
					isLoading={isLoading}
					onChangeRecaptcha={onChangeRecaptcha}
					handleSubmit={handleSubmit}
					handleSubmitData={handleSubmitData}
					hideRecaptcha={showDeactivatedMessage || showReactivateAccountLink}
					hideSSO={isSocialRecover}
				>
					<div className="space-y-6">
						{/* Input - email */}
						<InputEmail
							dataCY="email"
							name="email"
							title="Email"
							register={register}
							rules={rules.email}
							error={errors.email}
							setValueCustom={setValue}
						/>

						{/* Input - password */}
						<InputPassword
							dataCY="password"
							name="password"
							title="Password"
							validate={false}
							register={register}
							rules={rules.password}
							error={errors.password}
							className="my-4"
						/>
						{errorMessage ? (
							<Alert
								text={
									<div className="flex items-center gap-1">
										<Typography
											type={'regular-18'}
											className={clsx("'text-main-red'", {
												'text-main-blue-dark':
													typeof errorMessage === 'string' &&
													(errorMessage.includes('verify') ||
														errorMessage.includes('reactivate')),
											})}
										>
											{errorMessage}
											{typeof errorMessage === 'string' &&
												errorMessage?.includes('reactivate') && (
													<Link
														href={AppRoutes.CLIENTAPP_AUTH_REACTIVATE_ACCOUNT}
														className="inline-block font-medium lg:font-semibold text-main-blue-dark"
													>
														Click here
													</Link>
												)}
										</Typography>
									</div>
								}
								typography="regular-16"
								className="my-2"
								type={
									typeof errorMessage === 'string' &&
									(errorMessage.includes('verify') ||
										errorMessage.includes('reactivate'))
										? 'info'
										: 'error'
								}
							/>
						) : null}

						{showReactivateAccountLink && (
							<Alert
								typography="regular-18"
								text={
									<div>
										You deleted your account.
										<br />
										Would you like to reactivate it?{' '}
										<Link
											href={AppRoutes.CLIENTAPP_AUTH_REACTIVATE_ACCOUNT}
											className="inline-block  font-medium lg:font-semibold text-main-blue-dark"
										>
											Click here
										</Link>
									</div>
								}
								className="my-2 text-main-blue-dark"
							/>
						)}

						{showDeactivatedMessage && (
							<Alert
								text={
									'Your account has been deactivated and you are unable to sign at this time'
								}
								className="my-2"
							/>
						)}

						{!showDeactivatedMessage && !showReactivateAccountLink && (
							<div className="flex flex-col sm:flex-row gap-2 sm:items-center justify-between w-full">
								<InputCheck
									name="rememberme"
									register={register}
									defaultChecked={isRememberMe}
								>
									Remember me
								</InputCheck>

								{isVerified &&
									!showReactivateAccountLink &&
									!showDeactivatedMessage && (
										<div className="flex">
											<Link href={AppRoutes.CLIENTAPP_AUTH_FORGOT_PASSWORD}>
												<Typography
													type="semibold-16"
													className="text-primary hover:opacity-60 text-right"
												>
													Forgot your password?
												</Typography>
											</Link>
										</div>
									)}
							</div>
						)}
					</div>
				</AuthCard>
			)}
		</AuthLayout>
	);
};
